import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { appointmentsService } from '../infrastructure/appointments.service';
import type { AppointmentListParams } from '../infrastructure/appointments.service';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import type {
  CreateAppointmentInput,
  UpdateAppointmentInput,
  CancelAppointmentInput,
  SlotQueryParams,
  BookAppointmentInput,
  AppointmentResponseDto,
} from '../domain/types';

export const appointmentKeys = {
  all: ['appointments'] as const,
  lists: () => [...appointmentKeys.all, 'list'] as const,
  list: (params: AppointmentListParams) => [...appointmentKeys.lists(), params] as const,
  /** Full range fetch (pages until exhausted; API max page size 200). */
  listAll: (params: Omit<AppointmentListParams, 'page' | 'limit'>) =>
    [...appointmentKeys.lists(), 'all', params] as const,
  details: () => [...appointmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...appointmentKeys.details(), id] as const,
  slots: (params: SlotQueryParams) => [...appointmentKeys.all, 'slots', params] as const,
  queue: () => [...appointmentKeys.all, 'queue'] as const,
  availability: (clinicId: string, doctorId: string, date: string, procedureId?: string, durationOverride?: number, operatoryId?: string) =>
    ['appointments', clinicId, 'availability', doctorId, date, procedureId, durationOverride, operatoryId] as const,
  queueList: (date: string, doctorId?: string) =>
    ['appointments', 'queue-list', date, doctorId] as const,
};

/** API Zod max for list `limit` — keep calendar / range fetches within this. */
const APPOINTMENT_LIST_MAX_LIMIT = 200;
const APPOINTMENT_LIST_ALL_MAX_PAGES = 25;

/**
 * Shared list params for patient overview "Total Visits" + recent timeline.
 * Identical keys so React Query dedupes to one network request.
 */
export function patientCompletedVisitsListParams(
  patientId: string,
): AppointmentListParams {
  return {
    patientId,
    status: 'completed',
    limit: 8,
    sortBy: 'scheduled_at',
    sortOrder: 'desc',
  };
}

export function useAppointmentList(
  params: AppointmentListParams,
  enabledOrOptions: boolean | { enabled?: boolean; keepPrevious?: boolean } = true,
) {
  const options =
    typeof enabledOrOptions === 'boolean'
      ? { enabled: enabledOrOptions }
      : enabledOrOptions;

  return useQuery({
    queryKey: appointmentKeys.list(params),
    queryFn: () => appointmentsService.list(params),
    staleTime: QUERY_STALE_MS.HOT,
    enabled: options.enabled ?? true,
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
  });
}

/** Completed visits for a patient (header visit count + overview timeline). */
export function usePatientCompletedVisits(patientId: string | undefined) {
  const params = patientCompletedVisitsListParams(patientId ?? '');
  return useAppointmentList(params, !!patientId);
}

/**
 * Fetches every appointment in a date range by paging at the API max (200).
 * Used by calendar Month (and other range views) where a single page may truncate.
 */
export function useAppointmentListAll(
  params: Omit<AppointmentListParams, 'page' | 'limit'>,
) {
  return useQuery({
    queryKey: appointmentKeys.listAll(params),
    queryFn: async () => {
      const collected: Awaited<ReturnType<typeof appointmentsService.list>>['data'] = [];
      let page = 1;
      let total = 0;
      let hasMore = true;

      while (hasMore && page <= APPOINTMENT_LIST_ALL_MAX_PAGES) {
        const res = await appointmentsService.list({
          ...params,
          page,
          limit: APPOINTMENT_LIST_MAX_LIMIT,
        });
        collected.push(...res.data);
        total = res.total;
        hasMore = res.hasMore;
        page += 1;
      }

      return {
        data: collected,
        total,
        page: 1,
        limit: collected.length,
        hasMore: false,
      };
    },
  });
}

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: appointmentKeys.detail(id!),
    queryFn: () => appointmentsService.getById(id!),
    enabled: !!id,
  });
}

export function useAppointmentSlots(params: SlotQueryParams) {
  return useQuery({
    queryKey: appointmentKeys.slots(params),
    queryFn: () => appointmentsService.getSlots(params),
    enabled: !!params.providerId && !!params.date,
  });
}

export function useTodayQueue() {
  return useQuery({
    queryKey: appointmentKeys.queue(),
    queryFn: () => appointmentsService.todayQueue(),
  });
}

export function useDoctorAvailability(
  clinicId: string,
  doctorId: string,
  date: string,
  procedureId?: string,
  durationOverride?: number,
  operatoryId?: string,
) {
  return useQuery({
    queryKey: appointmentKeys.availability(clinicId, doctorId, date, procedureId, durationOverride, operatoryId),
    queryFn: () =>
      appointmentsService.getDoctorAvailability(doctorId, {
        date,
        procedureId,
        durationOverride,
        operatoryId,
      }),
    staleTime: QUERY_STALE_MS.HOT,
    enabled: !!doctorId && !!date,
  });
}

export function useAppointmentQueue(
  date: string,
  doctorId?: string,
) {
  return useQuery({
    queryKey: appointmentKeys.queueList(date, doctorId),
    queryFn: () =>
      appointmentsService.listTrackC({
        date,
        doctorId: doctorId || undefined,
        status: ['scheduled', 'confirmed', 'checked_in', 'in_progress'],
      }),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

export function useBookAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, key }: { input: BookAppointmentInput; key?: string }) =>
      appointmentsService.bookAppointment(input, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.queue() });
    },
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, key }: { input: CreateAppointmentInput; key?: string }) =>
      appointmentsService.create(input, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.queue() });
    },
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
      version,
    }: {
      id: string;
      input: UpdateAppointmentInput;
      version: number;
    }) => appointmentsService.update(id, input, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.details() });
    },
  });
}

export function useTransitionStatus(date?: string, doctorId?: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, {
    id: string;
    action: 'confirm' | 'check-in' | 'start' | 'complete' | 'no-show';
  }, { prev: unknown } | undefined>({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: 'confirm' | 'check-in' | 'start' | 'complete' | 'no-show';
    }) => {
      switch (action) {
        case 'confirm':
          return appointmentsService.confirm(id);
        case 'check-in':
          return appointmentsService.checkIn(id);
        case 'start':
          return appointmentsService.startAppointment(id);
        case 'complete':
          return appointmentsService.completeAppointment(id);
        case 'no-show':
          return appointmentsService.noShow(id);
      }
    },

    onMutate: async ({ id, action }) => {
      if (!date) return;
      const listKey = appointmentKeys.queueList(date, doctorId);
      await queryClient.cancelQueries({ queryKey: listKey });
      const prev = queryClient.getQueryData(listKey);

      queryClient.setQueryData(listKey, (old: unknown) => {
        const oldData = old as { data: AppointmentResponseDto[] } | undefined;
        if (!oldData?.data) return old;
        return {
          ...oldData,
          data: oldData.data.map((a: AppointmentResponseDto) =>
            a.id === id
              ? {
                  ...a,
                  status: action === 'start'
                    ? 'in_progress'
                    : action === 'complete'
                      ? 'completed'
                      : action === 'check-in'
                        ? 'checked_in'
                        : action === 'no-show'
                          ? 'no_show'
                          : action === 'confirm'
                            ? 'confirmed'
                            : a.status,
                }
              : a,
          ),
        };
      });

      return { prev };
    },

    onError: (_err, _vars, ctx) => {
      if (!date) return;
      const listKey = appointmentKeys.queueList(date, doctorId);
      if (ctx?.prev) {
        queryClient.setQueryData(listKey, ctx.prev);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.details() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.queue() });
      if (date) {
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.queueList(date, doctorId),
        });
      }
    },
  });
}

export function useCancelAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CancelAppointmentInput }) =>
      appointmentsService.cancel(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.details() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.queue() });
    },
  });
}

export function useCheckPatientActiveAppointment(patientId: string | undefined) {
  return useQuery({
    queryKey: [...appointmentKeys.all, 'active-for-patient', patientId] as const,
    queryFn: () => appointmentsService.checkActiveAppointment(patientId!),
    enabled: !!patientId,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => appointmentsService.softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.details() });
    },
  });
}
